/**
 * MoyRi — the five core ORDER seats, the ORDER Health Check, and the enriched
 * SWAP content. MoyRi's whole job: find which seats a malady holds, build the
 * SWAP to that seat's deity automatically (never "pick a deity"), and watch
 * real tool use push the spectrum toward the deity.
 *
 * Two numbers, two jobs (never merged) — Update Directive 18 Oct 2026 §2–5:
 *  - SEAT SCORE (−4..+4, seatScore.ts / useSeats.ts) = what you've actually been
 *    doing lately: 4 × (D − M) ÷ marks due over the last 30 days. Seats START AT
 *    0 and show "—" until 8 days of marks. Who holds the seat is the SIGN: above
 *    0 the deity, below 0 the malady; the flip happens at 0 — no buffer, no
 *    hysteresis, no forgiveness. The magnitude is how firmly.
 *  - ACQUISITION (deity-progress.ts ladder: Day → Aim → Goal → Acquired) = what
 *    you've proven you can do. A user can have KYRS acquired while Koalemos
 *    holds Direct ("Acquired · unseated").
 *
 * Storage: a health check is a `dmt_intake` row (answers.kind =
 * 'order-health-check') and records `dmt_state` (malady_subtype + intake
 * position) via saveIntake(). The intake severity is CONTEXT (how entrenched
 * the malady is) — it never sets the seat; dmt_state.displayed_position is no
 * longer read for the seat.
 *
 * Seat maladies are the ones whose SWAP_DATA entry belongs to the seat's own
 * tool, so the replacement content always matches the replacing deity.
 */
import type { GearId } from './ascension';
import type { DmtToolId } from './spectrum-tools';

export interface MoyriSeat {
  gear: GearId;
  /** The tool that carries this seat's deity (art, levels, SWAP_DATA, telemetry). */
  toolId: DmtToolId;
  /** Seat deity name as MoyRi shows it. */
  name: string;
  /** Maladies that can hold this seat (ids within `toolId`). First = primary. */
  maladies: string[];
  /** What MoyRi reads as a rep for this seat. */
  watch: string;
}

export const MOYRI_SEATS: MoyriSeat[] = [
  {
    gear: 'orient',
    toolId: 'telos',
    name: 'ATLAS',
    maladies: ['plane'],
    watch: 'Whether your actions stay tied to the chosen direction, and whether you reorient after drift.',
  },
  {
    gear: 'regulate',
    toolId: 'numa',
    name: 'POROS',
    maladies: ['oizys', 'phthisis', 'aergia'],
    watch: 'Whether resource gaps (energy, time, money, help) are found and resolved before execution.',
  },
  {
    gear: 'direct',
    toolId: 'kyrs',
    name: 'KYRS',
    maladies: ['koalemos', 'oknos'],
    watch: 'Whether prioritising uses the Direct method (the scored list) rather than noise or urgency.',
  },
  {
    gear: 'execute',
    toolId: 'metis',
    name: 'METIS',
    maladies: ['ate'],
    watch: 'Whether the selected Step and its sequence are followed, and recovered after disruption.',
  },
  {
    gear: 'revise',
    toolId: 'clio',
    name: 'CLIO',
    maladies: ['lethe'],
    watch: 'Whether review actually happens, and whether it causes a revision.',
  },
];

export const SEAT_BY_GEAR = Object.fromEntries(MOYRI_SEATS.map((s) => [s.gear, s])) as Record<GearId, MoyriSeat>;
export const SEAT_BY_TOOL = Object.fromEntries(MOYRI_SEATS.map((s) => [s.toolId, s])) as Partial<Record<DmtToolId, MoyriSeat>>;

/**
 * Who governs the seat at a §5 seat score: the SIGN decides, flipping at exactly
 * 0 (no buffer). `null` ("—", under 8 days of marks) holds nobody yet; exactly 0
 * is 'contested'.
 */
export function seatHolder(position: number | null): 'deity' | 'malady' | 'contested' | null {
  if (position == null) return null;
  return position > 0 ? 'deity' : position < 0 ? 'malady' : 'contested';
}

/** Severity 1..4 of a malady-held seat (0 when the malady doesn't hold it). */
export function severityOf(position: number | null): number {
  return position != null && position < 0 ? Math.max(1, Math.min(4, Math.round(-position))) : 0;
}

/** Intake severity (dmt_intake.start_position) as context text — never the seat. */
export function intakeWord(startPosition: number | null): string {
  if (startPosition == null) return 'not checked';
  if (startPosition > 0) return 'healthy';
  if (startPosition <= -3) return 'entrenched';
  if (startPosition === -2) return 'established';
  return 'mild';
}

export function spectrumWord(position: number | null): string {
  if (position == null) return 'Not enough marks yet';
  if (position <= -3.5) return 'Malady dominates';
  if (position <= -1.5) return 'Malady usually wins';
  if (position < 0) return 'Malady edge';
  if (position === 0) return 'Contested';
  if (position <= 1.5) return 'Deity edge';
  if (position <= 3) return 'Deity usually wins';
  return 'Deity dominates';
}

// ---------------------------------------------------------------------------
// ORDER Health Check
// ---------------------------------------------------------------------------

export const FREQ_OPTIONS = ['Never', 'Rarely', 'Sometimes', 'Usually'] as const;

export interface CheckItem {
  text: string;
  /** Malady this statement points at (absent on deity-sign items). */
  leans?: string;
  /** Deity-sign statement: "Usually" is healthy, so the score is reversed. */
  reverse?: boolean;
}

/** 8 broad frequency questions per seat — every seat malady touched, plus deity signs. */
export const BROAD: Record<GearId, CheckItem[]> = {
  orient: [
    { text: 'I’m busy all day but couldn’t say what it’s all for.', leans: 'plane' },
    { text: 'What I work on is decided by whatever is loudest in front of me.', leans: 'plane' },
    { text: 'I say yes to requests without checking whether they fit where I’m going.', leans: 'plane' },
    { text: 'My direction changes with my mood.', leans: 'plane' },
    { text: 'I avoid setting goals because committing to one feels risky.', leans: 'plane' },
    { text: 'I can name the aim my work this week is serving.', reverse: true },
    { text: 'When I notice I’ve drifted, I steer back to my aim the same day.', reverse: true },
    { text: 'My projects this year connect into one story.', reverse: true },
  ],
  regulate: [
    { text: 'I keep going until I physically can’t, then crash.', leans: 'oizys' },
    { text: 'Resting makes me feel guilty or lazy.', leans: 'oizys' },
    { text: 'My energy and reserves feel thinner than a few months ago, with no clear cause.', leans: 'phthisis' },
    { text: 'Weeks go by without me checking how much capacity I actually have.', leans: 'phthisis' },
    { text: 'I leave days light even when I have energy to spare.', leans: 'aergia' },
    { text: 'My comfortable pace has become my only pace.', leans: 'aergia' },
    { text: 'I check what’s in the tank before loading up the day.', reverse: true },
    { text: 'I spot shortages of time, money, energy or help before I start — not halfway through.', reverse: true },
  ],
  direct: [
    { text: 'The loudest or nearest task wins, not the most important one.', leans: 'koalemos' },
    { text: 'I start working before deciding what matters most.', leans: 'koalemos' },
    { text: 'Small urgent requests push my real priority back.', leans: 'koalemos' },
    { text: 'I end the day busy, with the big task untouched.', leans: 'koalemos' },
    { text: 'I re-sort and re-plan my list instead of starting the top item.', leans: 'oknos' },
    { text: 'My top priority keeps rotating without getting done.', leans: 'oknos' },
    { text: 'Planning feels productive enough that doing gets put off.', leans: 'oknos' },
    { text: 'I work my task list from the top down.', reverse: true },
  ],
  execute: [
    { text: 'My day has no shape before it starts.', leans: 'ate' },
    { text: 'I spend the day reacting instead of following a plan.', leans: 'ate' },
    { text: '“Where did the day go?” is a question I ask.', leans: 'ate' },
    { text: 'After an interruption, I don’t get back to what I was doing.', leans: 'ate' },
    { text: 'I skip steps or do them out of order and pay for it later.', leans: 'ate' },
    { text: 'Time leaks away unaccounted for.', leans: 'ate' },
    { text: 'I lay out today’s sequence before the first task.', reverse: true },
    { text: 'When the plan breaks, I pick up the next step instead of dropping it.', reverse: true },
  ],
  revise: [
    { text: 'I go to bed without looking back at the day.', leans: 'lethe' },
    { text: 'I make the same mistake more than once.', leans: 'lethe' },
    { text: 'I can’t say what worked last week.', leans: 'lethe' },
    { text: 'I avoid looking at things that went badly.', leans: 'lethe' },
    { text: 'Lessons I notice don’t change what I do next.', leans: 'lethe' },
    { text: 'Wins disappear from memory before I can reuse them.', leans: 'lethe' },
    { text: 'I review the day or the week.', reverse: true },
    { text: 'Something I learned last week changed how I work this week.', reverse: true },
  ],
};

/** Branch questions — asked only when the broad pass shows dysfunction. */
export const SPECIFIC: Record<string, string[]> = {
  plane: [
    'I’ve spent a week on things that, looking back, served no aim I hold.',
    'If someone asked where I’ll be in two years, I’d have no real answer.',
    'I jump to new projects before old ones get anywhere.',
  ],
  oizys: [
    'I push past tiredness with caffeine, adrenaline or deadlines.',
    'I burn out on a predictable cycle.',
    'I feel either fully on or completely empty, rarely in between.',
  ],
  phthisis: [
    'Something is slowly draining me — money, health, sleep, a relationship — and I haven’t dealt with it.',
    'I can do less than I used to, but no single day explains it.',
    'Small leaks (subscriptions, late nights, unfinished admin) pile up unattended.',
  ],
  aergia: [
    'I hold back effort so I never find out where my limit is.',
    'I choose the easy version of a task even when I could do the full one.',
    'Ambitions stay on the someday list while my days stay comfortable.',
  ],
  koalemos: [
    'Notifications and messages decide what I do next.',
    'I pick tasks by how quick they are, not by how much they matter.',
    'I feel decisive while doing the wrong thing.',
  ],
  oknos: [
    'I’ve rewritten the same plan or list more than twice this month.',
    'My list is well organised and barely touched.',
    'I prepare and research instead of starting.',
  ],
  ate: [
    'I rush into things without thinking through the order.',
    'Unplanned stuff eats most of my day.',
    'I abandon the plan at the first disruption.',
  ],
  lethe: [
    'I don’t record what happened or what I’d change.',
    'I lose track of what I promised or decided.',
    'I start each week from scratch, as if last week didn’t happen.',
  ],
};

/** Broad dysfunction below this ratio = the deity holds; no malady is invented. */
const HEALTHY_BELOW = 0.4;

export interface SeatResult {
  gear: GearId;
  holder: 'deity' | 'malady';
  /** Diagnosed malady (or the seat's primary malady when the deity holds — the opponent on the line). */
  maladyId: string;
  /** 0 when healthy, else 1..4. */
  severity: number;
  confidence: 'high' | 'medium' | 'low';
  /** Intake context only (recorded as start_position, never the seat): +1/+2 healthy, −severity otherwise. */
  position: number;
  /** 0..1 broad dysfunction ratio. */
  broad: number;
}

/** 0..3 dysfunction value of one answer (answers are FREQ_OPTIONS indices). */
function dys(item: CheckItem, answer: number): number {
  return item.reverse ? 3 - answer : answer;
}

export function broadRatio(gear: GearId, answers: number[]): number {
  const items = BROAD[gear];
  const sum = items.reduce((acc, it, i) => acc + dys(it, answers[i] ?? 0), 0);
  return sum / (items.length * 3);
}

/** Does the broad pass call for the branch questions? */
export function needsBranch(gear: GearId, answers: number[]): boolean {
  return broadRatio(gear, answers) >= HEALTHY_BELOW;
}

/** Malady that leans hardest in the broad answers (ties → seat order). */
function broadLeader(gear: GearId, answers: number[]): string {
  const seat = SEAT_BY_GEAR[gear];
  let best = seat.maladies[0];
  let bestScore = -1;
  for (const m of seat.maladies) {
    const idx = BROAD[gear].map((it, i) => (it.leans === m ? i : -1)).filter((i) => i >= 0);
    const score = idx.length ? idx.reduce((a, i) => a + (answers[i] ?? 0), 0) / idx.length : 0;
    if (score > bestScore) {
      best = m;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Fold one seat's answers into a result. `specific` maps maladyId → answers to
 * that malady's branch questions (omitted when the broad pass was healthy).
 */
export function scoreSeat(gear: GearId, broadAnswers: number[], specific?: Record<string, number[]>): SeatResult {
  const seat = SEAT_BY_GEAR[gear];
  const broad = broadRatio(gear, broadAnswers);
  const healthy = (b: number): SeatResult => ({
    gear,
    holder: 'deity',
    maladyId: broadLeader(gear, broadAnswers),
    severity: 0,
    confidence: b < 0.2 ? 'high' : 'medium',
    position: b < 0.2 ? 2 : 1,
    broad: b,
  });
  if (broad < HEALTHY_BELOW || !specific) return healthy(broad);

  const ratios = seat.maladies
    .map((m) => {
      const a = specific[m] ?? [];
      return { m, r: a.length ? a.reduce((x, y) => x + y, 0) / (a.length * 3) : 0 };
    })
    .sort((a, b) => b.r - a.r);
  const top = ratios[0];
  const combined = (broad + top.r) / 2;
  // The branch questions can overturn a shaky broad pass — never invent a malady.
  if (combined < HEALTHY_BELOW) return healthy(broad);

  const severity = combined >= 0.85 ? 4 : combined >= 0.7 ? 3 : combined >= 0.55 ? 2 : 1;
  const margin = ratios.length > 1 ? top.r - ratios[1].r : 1 - Math.abs(top.r - broad);
  const confidence = ratios.length > 1
    ? margin >= 0.25 ? 'high' : margin >= 0.1 ? 'medium' : 'low'
    : margin >= 0.8 ? 'high' : margin >= 0.65 ? 'medium' : 'low';
  return { gear, holder: 'malady', maladyId: top.m, severity, confidence, position: -severity, broad };
}

// ---------------------------------------------------------------------------
// Enriched SWAP content (layered on SWAP_DATA's why / symptoms / S·W·A·P)
// ---------------------------------------------------------------------------

export interface SwapExtra {
  deitySigns: string[];
  dissolveNow: string;
  keepItGone: string[];
  cost: string;
  howYouGotHere: string;
  whatMakesItWorse: string;
}

export const SWAP_EXTRA: Record<string, SwapExtra> = {
  plane: {
    deitySigns: [
      'You can name the aim this week serves',
      'New requests get checked against the aim before you say yes',
      'Drift gets caught the same day and steered back',
    ],
    dissolveNow: 'Open ATLAS, read the aim, and pick the one next action that serves it.',
    keepItGone: [
      'Aim on the lock screen — read it before the first task',
      'Every new project gets tied to an aim, or dropped',
      'Sunday: re-read the aim and check the week against it',
    ],
    cost: 'Years of motion that add up to nothing you chose. Other people’s priorities fill the space your own aim left empty.',
    howYouGotHere: 'Committing to a direction once went wrong, or was never modelled. Keeping options open felt safer than being wrong.',
    whatMakesItWorse: 'Open-ended days, constant inputs, saying yes to keep the peace, and no written aim to check against.',
  },
  oizys: {
    deitySigns: [
      'You check the tank before loading the day',
      'Rest is planned, not earned by collapse',
      'Output holds steady across weeks — no crash cycle',
    ],
    dissolveNow: 'Open POROS, log your real energy, and cut today’s load to fit it.',
    keepItGone: [
      'Energy check before anything gets added to the day',
      'A hard stop time you don’t negotiate with',
      'Recovery blocks on the calendar, like meetings',
    ],
    cost: 'Today’s output paid for with next week’s collapse — and health, mood and relationships take the hit in between.',
    howYouGotHere: 'Your worth got tied to output. Pushing through was praised; stopping felt like failing.',
    whatMakesItWorse: 'Deadlines, late-night second winds, caffeine, and lists that grow faster than they shrink.',
  },
  phthisis: {
    deitySigns: [
      'Capacity gets checked weekly, not just in a crisis',
      'Small leaks get fixed while they are still small',
      'Reserves — sleep, money, health — hold or grow',
    ],
    dissolveNow: 'Open POROS, name the one leak draining you most, and schedule its fix this week.',
    keepItGone: [
      'A standing weekly capacity check',
      'One leak fixed per week',
      'Watch the slope, not the single day',
    ],
    cost: 'A slow slide nobody notices until it becomes a breakdown — far more expensive than fixing it early.',
    howYouGotHere: 'Nothing ever forced the issue. Slow declines never trip an alarm, so they never got addressed.',
    whatMakesItWorse: 'Busy seasons, no measurement, and telling yourself it’s just a rough patch.',
  },
  aergia: {
    deitySigns: [
      'Load matches the gauge on high-energy days',
      'You attempt the full version, not just the safe one',
      'You know roughly where your ceiling is',
    ],
    dissolveNow: 'Open POROS, read today’s gauge, and add the one stretch task it can carry.',
    keepItGone: [
      'Match load to the gauge in both directions',
      'One stretch task scheduled every week',
      'Review: what could today have taken that it didn’t?',
    ],
    cost: 'A life sized to comfort, not capacity. Ambitions quietly expire unattempted.',
    howYouGotHere: 'Not trying protected you from finding a limit. Comfort became a habit that felt like safety.',
    whatMakesItWorse: 'No stretch goals, easy defaults, and no feedback on what you could actually carry.',
  },
  koalemos: {
    deitySigns: [
      'The scored top item gets worked first',
      'Small urgent requests wait their turn',
      'End of day: the big rock moved',
    ],
    dissolveNow: 'Open KYRS and start the top-ranked task — not the loudest one.',
    keepItGone: [
      'Score the list before the day starts',
      'Notifications off during the top task',
      'Small requests batched into one slot',
    ],
    cost: 'Busy every day, while the work that matters most never moves.',
    howYouGotHere: 'Deciding felt uncomfortable; grabbing the nearest task gave instant relief and looked like productivity.',
    whatMakesItWorse: 'Open inboxes, unranked lists, and an environment built for interruption.',
  },
  oknos: {
    deitySigns: [
      'The top item has a start time — and starts on it',
      'The list gets worked more than it gets re-sorted',
      'Finished work outnumbers re-planned work',
    ],
    dissolveNow: 'Open KYRS, give the top item a start time inside the next hour, and start it.',
    keepItGone: [
      'Re-plan at most once a day',
      'Every top item gets a start time',
      'Three days in the top rank means stuck — break it down',
    ],
    cost: 'Effort spent and never landing. Plans pile up; results never arrive.',
    howYouGotHere: 'Preparing felt safe and productive. Starting risked doing it wrong, so planning took its place.',
    whatMakesItWorse: 'New planning apps, perfectionism, and lists with no start times.',
  },
  ate: {
    deitySigns: [
      'The day is sequenced before the first task',
      'After an interruption, you resume the next step',
      'You can account for where the day went',
    ],
    dissolveNow: 'Open METIS, lay out the next three steps in order, and start step one.',
    keepItGone: [
      'Sequence tomorrow before you close today',
      'Leave a resume note whenever you get interrupted',
      'Buffer slots for the unplanned stuff',
    ],
    cost: 'Days lost to reaction. Rushed, out-of-order work that has to be done twice.',
    howYouGotHere: 'An open day felt free, and no plan meant no failure — so structure never got built.',
    whatMakesItWorse: 'Reactive work, constant interruptions, and starting the day in messages.',
  },
  lethe: {
    deitySigns: [
      'A review happens at the end of the day or week',
      'One lesson changes one thing next week',
      'Wins get written down and reused',
    ],
    dissolveNow: 'Open CLIO and run a five-minute review: what worked, what didn’t, one change.',
    keepItGone: [
      'A standing night review in CLIO',
      'Every review ends with one change to try',
      'Check last week’s change before setting this week',
    ],
    cost: 'The same mistakes, repeated. Wins that never compound because nobody remembers them.',
    howYouGotHere: 'Looking back meant facing the misses. Moving straight on avoided the sting.',
    whatMakesItWorse: 'Packed calendars, no closing ritual, and shame about what went wrong.',
  },
};
